import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthStore } from '@app/stores/auth.store';
import { filter, map, take } from 'rxjs';

export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  const resolve = (): boolean | ReturnType<Router['createUrlTree']> => {
    if (!authStore.isLoggedIn()) {
      return router.createUrlTree(['/auth/login'], {
        queryParams: { returnUrl: state.url },
      });
    }
    return true;
  };

  // wait for any in-progress session restore to complete before making an auth decision
  // without this, the guard see only isLoggedIn=false while the token refresh with fetchUser api calls are still in-floght
  if (!authStore.isLoading()) {
    return resolve();
  }

  return toObservable(authStore.isLoading).pipe(
    filter((loading) => !loading),
    take(1),
    map(() => resolve()),
  );
};

export const basicRegistrationGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  const resolve = (): boolean | ReturnType<Router['createUrlTree']> => {
    if (!authStore.isBasicRegistrationCompleted()) {
      return router.createUrlTree(['/auth/registration/wizard']);
    }
    return true;
  };

  if (!authStore.isLoading()) {
    return resolve();
  }

  return toObservable(authStore.isLoading).pipe(
    filter((loading) => !loading),
    take(1),
    map(() => resolve()),
  );
};

export const qulificationCompletedGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  if (authStore.isRegisteredPersonalUser() || authStore.isAdmin()) {
    if (!authStore.isQualificationCompleted()) {
      router.navigateByUrl('/investor-qualification-test');
      return false;
    }

    return true;
  }

  return verificationGuard(route, state);
};

export const legalPersonGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  const canAccessLegalPersonRoutes = (): boolean => {
    return (
      authStore.isRegisteredLegalPerson() ||
      authStore.isAdmin() ||
      !!authStore.loggedInCompanyId()
    );
  };

  const resolve = (): boolean | ReturnType<Router['createUrlTree']> => {
    if (canAccessLegalPersonRoutes()) {
      return true;
    }
    return router.createUrlTree(['/profile']);
  };

  if (!authStore.isLoading()) {
    return resolve();
  }

  return toObservable(authStore.isLoading).pipe(
    filter((loading) => !loading),
    take(1),
    map(() => resolve()),
  );
};

export const verificationGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  const resolve = (): boolean | ReturnType<Router['createUrlTree']> => {
    if (
      authStore.isLoggedIn() &&
      !authStore.isVerified() &&
      !authStore.isAdmin()
    ) {
      return router.createUrlTree(['/auth/registration/wizard']);
    }
    const result = authGuard(route, state);
    return result as boolean | ReturnType<Router['createUrlTree']>;
  };

  if (!authStore.isLoading()) {
    return resolve();
  }

  return toObservable(authStore.isLoading).pipe(
    filter((loading) => !loading),
    take(1),
    map(() => resolve()),
  );
};

export const adminGuard: CanActivateFn = () => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  const resolve = (): boolean | ReturnType<Router['createUrlTree']> => {
    if (!authStore.isAdmin()) {
      return router.createUrlTree(['/projects']);
    }
    return true;
  };

  if (!authStore.isLoading()) {
    return resolve();
  }

  return toObservable(authStore.isLoading).pipe(
    filter((loading) => !loading),
    take(1),
    map(() => resolve()),
  );
};
